/**
 * Communication hold switch for a claim (WPS-145).
 *
 * A communication hold pauses every automated email and text about a
 * claim so a person owns the correspondence instead. Owens Corning use it
 * for claims that have become legal matters and call theirs "Legal
 * Handling" - which is why nothing here is named for that: the label, the
 * helper text, and the permission that gates the switch all arrive on
 * ``claim.communication_hold`` from the API. A tenant with no hold
 * configured gets ``null`` and this renders nothing.
 *
 * Deliberately not part of the Claim Details card: that card is gated on
 * the claim being non-terminal and saves every field at once behind an
 * edit reason. A claim can turn into a legal matter after it closes, and
 * the switch has to stay reachable and one click away at any point in the
 * claim - so it is its own control, live in every state.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Scale } from 'lucide-react'
import { Switch } from '@/shared/components/ui/switch'
import { toast } from 'sonner'
import { toastApiError } from '@/shared/lib/api-error'
import { useEditClaimFields, type CommunicationHold } from '@/admin/hooks/use-claims'

interface CommunicationHoldControlProps {
  claimUuid: string
  hold: CommunicationHold | null | undefined
  /**
   * Where this is being rendered, which follows the hold's own state:
   *
   * - ``row`` (default) - the quiet control on Overview under "The Claim",
   *   used while the hold is OFF. A latent setting nobody needs to see from
   *   the Settlement tab.
   * - ``banner`` - the full-width bar above the tab bar, used while the hold
   *   is ON. At that point it has stopped being a setting and become a state
   *   of the claim that changes how you act on every tab, so it is the first
   *   thing on the page and carries its own off-switch.
   *
   * The caller renders exactly one of them, never both.
   */
  variant?: 'row' | 'banner'
}

export function CommunicationHoldControl({
  claimUuid,
  hold,
  variant = 'row',
}: CommunicationHoldControlProps) {
  const { t } = useTranslation()
  const editFields = useEditClaimFields()
  // Optimistic mirror so the switch moves under the finger; the claim
  // query refetch (onSuccess) is what ultimately settles it.
  const [pending, setPending] = useState<boolean | null>(null)

  if (!hold) return null

  const active = pending ?? hold.active

  const handleChange = async (next: boolean) => {
    setPending(next)
    try {
      await editFields.mutateAsync({
        uuid: claimUuid,
        attribute_values: { [hold.code_name]: next },
        edit_reason: next
          ? `${hold.label} switched on - automated messaging paused`
          : `${hold.label} switched off - automated messaging resumed`,
      })
      toast.success(
        next
          ? t(
              'claims.communicationHold.switchOnSuccess',
              '{{label}} is on. Automated messages about this claim are paused.',
              { label: hold.label },
            )
          : t(
              'claims.communicationHold.switchOffSuccess',
              '{{label}} is off. Automated messages about this claim have resumed.',
              { label: hold.label },
            ),
      )
    } catch (error) {
      setPending(null)
      toastApiError(
        error,
        t('claims.communicationHold.switchError', 'Could not change {{label}}.', {
          label: hold.label,
        }),
      )
    }
  }

  const toggle = hold.can_manage ? (
    <Switch
      checked={active}
      onCheckedChange={handleChange}
      disabled={editFields.isPending}
      aria-label={hold.label}
    />
  ) : (
    <span className="shrink-0 text-[12px] font-medium text-[var(--ink-2)]">
      {active
        ? t('claims.communicationHold.on', 'On')
        : t('claims.communicationHold.off', 'Off')}
    </span>
  )

  // ON: one full-width bar at the top of the claim. It leads with the
  // consequence ("automated messaging is paused") rather than the setting,
  // because that is what someone opening this claim needs to know before
  // they do anything else - and it carries its own off-switch so the state
  // and the control never live in two places.
  if (variant === 'banner') {
    return (
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[6px] border-[0.5px] border-l-[3px] border-[var(--status-warn)] border-l-[var(--status-warn)] bg-[var(--status-warn-bg)] px-4 py-2.5">
        <Scale className="h-4 w-4 shrink-0 text-[var(--status-warn)]" />
        <span className="text-[12px] font-medium uppercase tracking-[0.14em] text-[var(--status-warn)]">
          {hold.label}
        </span>
        <span className="min-w-0 flex-1 text-[12px] font-light text-[var(--ink-2)]">
          {t(
            'claims.communicationHold.banner',
            'Automated emails and texts about this claim are paused. Anything the customer needs to hear has to be sent by a person.',
          )}
        </span>
        {toggle}
      </div>
    )
  }

  // OFF: the quiet control on Overview. A left rule and a real label give it
  // the weight of the other "you can act here" rows, because a control nobody
  // notices is one nobody remembers to use.
  return (
    <div className="rounded-[8px] border-[0.5px] border-l-[3px] border-[var(--rule)] border-l-[var(--ink-3)] bg-[var(--bg-light)] px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <span className="mt-[1px] shrink-0 text-[var(--ink-2)]">
            <Scale className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <span className="truncate text-[12px] font-medium uppercase tracking-[0.14em] text-[var(--ink)]">
              {hold.label}
            </span>
            {hold.description ? (
              <p className="mt-1 text-[12px] leading-[1.5] text-[var(--ink-2)]">
                {hold.description}
              </p>
            ) : null}
          </div>
        </div>
        {toggle}
      </div>
    </div>
  )
}
